<script lang="ts" setup>
import { useScroll } from '@vueuse/core'

const props = defineProps<{
  name: string
  thumbsTrigger?: 'click' | 'mouseover'
}>()

const {
  galleryItems,
  activeIndex,
  getImageFromProvider,
} = useUiMediaGallery(props.name)

const thumbs = useTemplateRef('thumbs')
const scrollableThumbs = ref(false)

const {
  x,
  y,
  arrivedState,
} = useScroll(thumbs, { behavior: 'smooth' })

function onClick(index: number) {
  if (props.thumbsTrigger === 'click') {
    activeIndex.value = index
  }
}

function onMouseover(index: number) {
  if (props.thumbsTrigger === 'mouseover') {
    activeIndex.value = index
  }
}

function prevThumb() {
  if (thumbs.value?.clientWidth > thumbs.value?.clientHeight) {
    x.value += -(thumbs.value?.clientWidth || 0)
  }
  else {
    y.value += -(thumbs.value?.clientHeight || 0)
  }
}

function nextThumb() {
  if (thumbs.value?.clientWidth > thumbs.value?.clientHeight) {
    x.value += thumbs.value?.clientWidth || 0
  }
  else {
    y.value += thumbs.value?.clientHeight || 0
  }
}

const arrivedStatePrev = computed(() => {
  if (!arrivedState.left || !arrivedState.right) {
    return arrivedState.left
  }

  if (!arrivedState.top || !arrivedState.bottom) {
    return arrivedState.top
  }

  return true
})

const arrivedStateNext = computed(() => {
  if (!arrivedState.left || !arrivedState.right) {
    return arrivedState.right
  }

  if (!arrivedState.top || !arrivedState.bottom) {
    return arrivedState.bottom
  }

  return true
})

const targetIsVisible = useElementVisibility(thumbs)

function checkScrollablity() {
  const hor = (thumbs.value?.scrollWidth || 0) > (thumbs.value?.clientWidth || 0)
  const ver = (thumbs.value?.scrollHeight || 0) > (thumbs.value?.clientHeight || 0)
  scrollableThumbs.value = hor || ver
}

watchEffect(() => {
  if (targetIsVisible.value) {
    checkScrollablity()
  }
})

onMounted(() => {
  checkScrollablity()
})
</script>

<template>
  <div
    class="media-gallery-thumbs"
  >
    <button
      data-v-show="scrollableThumbs"
      type="button"
      class="media-gallery-thumbs__prev"
      :disabled="arrivedStatePrev"
      @click="prevThumb"
    >
      <slot name="thumbs-prev-icon">
        <NuxtIcon
          name="icon:arrow"
          class="icon:arrow-prev"
        />
      </slot>
    </button>

    <div
      ref="thumbs"
      class="media-gallery-thumbs__container"
    >
      <button
        v-for="item in galleryItems"
        :key="item.src"
        type="button"
        :data-thumb="item.index"
        class="media-gallery-thumbs__thumb"
        :class="{
          'media-gallery-thumbs__thumb--active': activeIndex === item.index,
        }"
        @click="onClick(item.index)"
        @mouseover="onMouseover(item.index)"
      >
        <UiVideo
          v-if="item.type === 'video'"
          :src="item.src"
          width="240"
          height="290"
          class="media-gallery-thumbs__video"
          :controls="false"
        />
        <img
          v-else-if="item.type === 'image'"
          :src="getImageFromProvider(item, { width: 240, height: 290 })"
          :alt="item.alt"
          width="240"
          height="290"
          loading="lazy"
          class="media-gallery-thumbs__image"
        >
      </button>
    </div>

    <button
      data-v-show="scrollableThumbs"
      type="button"
      class="media-gallery-thumbs__next"
      :disabled="arrivedStateNext"
      @click="nextThumb"
    >
      <slot name="thumbs-next-icon">
        <NuxtIcon
          name="icon:arrow"
          class="icon:arrow-next"
        />
      </slot>
    </button>
  </div>
</template>

<style>
@import url("~layers/components/Ui/MediaGallery/Thumbs.css");
</style>
